import { Helmet } from 'react-helmet-async';
import PageLayout from '@/components/layout/PageLayout';
import { SectionHeading } from '@/components/ui/section-heading';

const Taekwondo = () => {
  const timeline = [{
    year: "37 BCE–668 CE",
    title: "Three Kingdoms Period",
    description: "Early Korean martial traditions (Taekkyeon, Subak, Gwonbeop) referenced in murals and texts."
  }, {
    year: "918–1392",
    title: "Goryeo Dynasty",
    description: "Subak practised by military; emergence of organised striking methods."
  }, {
    year: "1392–1910",
    title: "Joseon Dynasty",
    description: "Decline of formal martial training; Taekkyeon survives culturally as folk practice."
  }, {
    year: "1910–1945",
    title: "Japanese Occupation",
    description: "Japanese arts (e.g., Shotokan Karate) influence training; many future kwan founders study in Japan."
  }, {
    year: "1944–1946",
    title: "First Kwans Established",
    description: "Song Moo Kwan (Ro Byung-jik) and Chung Do Kwan (Lee Won-kuk) open; strong karate roots."
  }, {
    year: "1947–1948",
    title: "Additional Kwans",
    description: "Moo Duk Kwan (Hwang Kee), Chang Moo Kwan (Yoon Byung-in), Yun Moo Kwan/Supreme Kwon Bop Bu expand curriculum with Tang Soo Do/Kwon Bop influence."
  }, {
    year: "1953–1955",
    title: "Military & Offshoot Kwans",
    description: "Oh Do Kwan (Choi Hong-hi & Nam Tae-hi), Ji Do Kwan (from Chosun Yun Moo Kwan), Kang Duk Won, Han Moo Kwan."
  }, {
    year: "1955",
    title: "Taekwon-Do Name Proposed",
    description: "General Choi Hong-hi proposes 'Taekwon-Do'; not all kwans adopt immediately."
  }, {
    year: "1958–1961",
    title: "Unification Efforts",
    description: "Kwans gradually unify under government guidance."
  }, {
    year: "1961",
    title: "KTA Founded",
    description: "Korean Taekwondo Association created to standardise the art."
  }, {
    year: "1963",
    title: "Moo Gong Ryu Founded",
    description: "Joon Pyo Choi is widely cited as founding Moo Gong Ryu in Korea, a style later carried forward through OMAC schools."
  }, {
    year: "1966",
    title: "ITF Formed",
    description: "Choi Hong-hi forms the ITF; Chang Hon patterns (Chon-Ji → Tong-Il) defined."
  }, {
    year: "1972",
    title: "Kukkiwon & Koryo Poomsae",
    description: "Kukkiwon (World Taekwondo HQ) opens in Seoul; the KTA poomsae committee codifies Koryo as the first black-belt form, symbolising mature power and the spirit of the Goryeo (Koryo) dynasty, while Choi relocates ITF leadership overseas."
  }, {
    year: "1973",
    title: "WTF (now WT) & Worlds",
    description: "World Taekwondo Federation formed; first World Taekwondo Championships held."
  }, {
    year: "1988 / 2000",
    title: "Olympic Milestones",
    description: "Olympic demonstration sport (Seoul 1988); official medal sport (Sydney 2000)."
  }, {
    year: "Today",
    title: "Modern Landscape",
    description: "WT (Kukkiwon/Taegeuk & Yudanja), ITF (Chang Hon), and Tang Soo Do/Soo Bahk Do (Moo Duk Kwan lineage)."
  }];

  return (
    <PageLayout>
      <Helmet>
        <title>What is Taekwondo? | Art, History, and Timeline</title>
        <meta name="description" content="Learn what Taekwondo is: its purpose, philosophy, and history—from ancient roots to modern practice—with a clear timeline of key milestones." />
        <meta name="keywords" content="What is Taekwondo, Taekwondo history, Taekwondo timeline, Song Moo Kwan, Korean martial arts, Kukkiwon, World Taekwondo, Olympic Taekwondo" />
        <meta property="og:title" content="What is Taekwondo? | Art, History, and Timeline" />
        <meta property="og:description" content="An introduction to Taekwondo—the art, its history, and a concise timeline of key milestones from ancient roots to the Olympics." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://omac-uk.co.uk/taekwondo" />
        <meta property="og:image" content="/images/history/opening_shot.jpeg" />
        <link rel="canonical" href="https://omac-uk.co.uk/taekwondo" />
      </Helmet>

      {/* Hero */}
      <div className="pt-16 pb-12 bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold font-display mb-6">Taekwondo</h1>
            <p className="text-xl text-gray-300">The art, its purpose, and how it came to be</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <SectionHeading subtitle="Introduction" title="What is Taekwondo?" alignment="left" gradient={true} />
              <p className="text-gray-700 mb-4">
                Taekwondo is a Korean striking art that brings together centuries-old kicking and hand techniques within a structured, modern training system. 
                People practise it for self-defence, better fitness, mental discipline and, for some, competition. At its heart it remains a traditional art built on respect, etiquette and personal development.
              </p>
              <p className="text-gray-700 mb-4">
                The word “Taekwondo” is often translated as “the way of the foot and the fist,” reflecting a curriculum built around efficient movement, timing, distance, and control. 
                Training develops flexibility, coordination, strength, and confidence, and can be adapted for children, adults, beginners, and advanced practitioners alike.
              </p>
              <p className="text-gray-700">
                Whether your goal is improved fitness, effective self-defence, or focused study of the art itself, Taekwondo offers a clear pathway of progression and a supportive community that encourages 
                steady effort and character development—on and off the mat.
              </p>
            </div>
            <div>
              <img src="/images/classes/adults_cover.jpeg" alt="Taekwondo practice" className="rounded-xl shadow-lg" />
            </div>
          </div>

          {/* Written History */}
          <div className="py-12">
            <SectionHeading subtitle="History" title="History of Taekwondo" gradient={true} />
            <div className="max-w-4xl mx-auto space-y-8 text-gray-700">
              {/* Pre-20th Century Foundations */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Pre-20th Century Foundations</h3>
                <h4 className="text-base font-semibold text-gray-800 mb-1">37 BCE – 668 CE — Three Kingdoms Period</h4>
                <p className="mb-3">
                  Early Korean martial traditions—Taekkyeon, Subak, and Gwonbeop—appear in murals and texts, reflecting native striking and grappling practices and a cultural appreciation for dynamic kicking.
                </p>
                <h4 className="text-base font-semibold text-gray-800 mb-1">918 – 1392 — Goryeo Dynasty</h4>
                <p className="mb-3">
                  Subak is practised by the military; sources reference codification of striking methods and training for soldiers and palace guards.
                </p>
                <h4 className="text-base font-semibold text-gray-800 mb-1">1392 – 1910 — Joseon Dynasty</h4>
                <p>
                  Formal martial training declines in a largely Confucian civil bureaucracy. Taekkyeon survives culturally as a folk art, preserving fluid kicking and footwork traditions.
                </p>
              </div>

              {/* Japanese Occupation & Modern Beginnings */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">1910 – 1945 — Japanese Occupation & Modern Beginnings</h3>
                <p>
                  Japan occupies Korea; Japanese arts—particularly Shotokan Karate, Judo, and Kendo—influence training. Many future founders of Korean schools study in Japan and return with pedagogy and fundamentals that intertwine with surviving Korean practices.
                </p>
              </div>

              {/* Founding of the Original Kwans */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">1944 – 1955 — Founding of the Original Korean Kwans</h3>
                <p className="mb-4">
                  These “mother schools” laid the modern technical and cultural foundations and later unified under the Taekwondo banner.
                </p>
                <h4 className="text-base font-semibold text-gray-800 mb-1">1944 – 1946</h4>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>
                    <span className="font-semibold">Song Moo Kwan</span> (Ro Byung-jik) — The first official kwan; karate-rooted fundamentals refined with Korean kicking emphasis.
                  </li>
                  <li>
                    <span className="font-semibold">Chung Do Kwan</span> (Lee Won-kuk) — Strong Shotokan base; known for disciplined basics, stances, and early leadership in unification.
                  </li>
                </ul>
                <h4 className="text-base font-semibold text-gray-800 mb-1">1947 – 1948</h4>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>
                    <span className="font-semibold">Moo Duk Kwan</span> (Hwang Kee) — Mixes Karate with Chinese arts; later associated with Tang Soo Do/Soo Bahk Do traditions.
                  </li>
                  <li>
                    <span className="font-semibold">Chang Moo Kwan</span> (Yoon Byung-in, later Lee Nam-suk) — Influenced by Shudokan Karate and Chinese Ch’uan-fa/Kwon Bop; adds circularity and diverse hand methods.
                  </li>
                  <li>
                    <span className="font-semibold">Yun Moo Kwan (Supreme Kwon Bop Bu)</span> — Focus on Kwon Bop (Chinese hand method), contributing to breadth of striking approaches.
                  </li>
                </ul>
                <h4 className="text-base font-semibold text-gray-800 mb-1">1953 – 1955</h4>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <span className="font-semibold">Oh Do Kwan</span> (Choi Hong-hi & Nam Tae-hi) — Military kwan; birthplace of early “Taekwon-Do” forms and demonstrations.
                  </li>
                  <li>
                    <span className="font-semibold">Ji Do Kwan</span> (from Chosun Yun Moo Kwan) — Striking emphasis with practical training.
                  </li>
                  <li>
                    <span className="font-semibold">Kang Duk Won, Han Moo Kwan</span> — Notable offshoots contributing instructors and curricula.
                  </li>
                </ul>
              </div>

              {/* Birth of Modern Taekwondo */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">1955 – 1961 — Birth of Modern Taekwondo</h3>
                <h4 className="text-base font-semibold text-gray-800 mb-1">1955</h4>
                <p className="mb-3">
                  General Choi Hong-hi proposes the name <span className="italic">Taekwon-Do</span>. Adoption is gradual; some kwans retain their own nomenclature initially.
                </p>
                <h4 className="text-base font-semibold text-gray-800 mb-1">1958 – 1961</h4>
                <p className="mb-3">
                  Under government guidance, kwans move toward unification.
                </p>
                <h4 className="text-base font-semibold text-gray-800 mb-1">1961</h4>
                <p>
                  The Korean Taekwondo Association (KTA) is formed to standardise dan certification, curriculum, and governance.
                </p>
              </div>

              {/* Split and Global Development */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">1966 – Today — The Split and Global Development</h3>
                <h4 className="text-base font-semibold text-gray-800 mb-1">International Taekwon-Do Federation (ITF)</h4>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li><span className="font-semibold">1966</span> — ITF founded by Choi Hong-hi; Chang Hon patterns (Chon-Ji → Tong-Il) codified.</li>
                  <li><span className="font-semibold">1972</span> — Choi leaves Korea; ITF HQ later moves to Vienna; multiple factions emerge after 2002.</li>
                </ul>
                <h4 className="text-base font-semibold text-gray-800 mb-1">World Taekwondo (WT) & Kukkiwon</h4>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>
                    <span className="font-semibold">1972</span> — Kukkiwon (World Taekwondo HQ) opens; the KTA poomsae committee adopts the Kukki curriculum (Taegeuk & Yudanja poomsae) and establishes Koryo as the first black-belt form, expressing mature power, leadership and the spirit of the Goryeo (Koryo) dynasty.
                  </li>
                  <li><span className="font-semibold">1973</span> — WTF (now WT) formed; first World Taekwondo Championships held.</li>
                  <li><span className="font-semibold">1988 / 2000</span> — Olympic milestones: demonstration in Seoul; official medal sport in Sydney.</li>
                </ul>
              </div>

              {/* Styles and Branching - Modern Landscape */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Modern Landscape (Concise)</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <span className="font-semibold">WT (World Taekwondo)</span> — Olympic style; Kukkiwon grading; poomsae: Taegeuk & Yudanja; strong sport framework.
                  </li>
                  <li>
                    <span className="font-semibold">ITF</span> — Traditional focus with 24 Chang Hon patterns; semi-contact sparring; multiple federations today.
                  </li>
                  <li>
                    <span className="font-semibold">Moo Gong Ryu</span> — A style attributed to Joon Pyo Choi, often cited as founded in Korea around 1963, and developed further through OMAC schools.
                  </li>
                  <li>
                    <span className="font-semibold">Tang Soo Do / Soo Bahk Do</span> — Descended from Moo Duk Kwan; karate-based with Korean identity; some lineages aligned with Kukkiwon as “Taekwondo Moo Duk Kwan.”
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div id="timeline" className="py-12">
            <SectionHeading subtitle="Summary" title="Historical Timeline" gradient={true} />
            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-200"></div>
              <div className="space-y-12">
                {timeline.map((item, index) => (
                  <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-taekwondo-red"></div>
                    <div className={`w-1/2 ${index % 2 === 0 ? 'pr-12 text-right' : 'pl-12'}`}>
                      <div className="bg-white p-6 rounded-xl shadow-md">
                        <div className="inline-block px-3 py-1 text-sm font-semibold bg-red-50 text-taekwondo-red rounded-full mb-2">
                          {item.year}
                        </div>
                        <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                    <div className="w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Taekwondo;


